import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider as OidcAuthProvider } from 'react-oidc-context';
import { Outlet, createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { AdminProtectedRoute } from './components/auth/AdminProtectedRoute';
import { CouponRedeemProtectedRoute } from './components/auth/CouponRedeemProtectedRoute';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import './index.css';
import { authConfig } from './lib/auth';
import { CouponDetailsPage } from './pages/CouponDetailsPage';
import { CouponRedeemPage } from './pages/CouponRedeemPage';
import { ErrorPage } from './pages/ErrorPage';
import { HomePage } from './pages/HomePage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { MyVouchersPage } from './pages/MyVouchersPage';
import AdminPage from './pages/admin/AdminPage';
import { Callback } from './pages/auth/Callback';
import { LoginPage } from './pages/auth/LoginPage';
import { BalanceHistoryPage } from './pages/balance/BalanceHistoryPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import ComponentsPage from './pages/index/ComponentsPage';
import { VoucherListPage } from './pages/vouchers/VoucherListPage';

const router = createBrowserRouter([
  {
    // Root route wrapper for error boundary
    element: <Outlet />,
    errorElement: <ErrorPage />,
    children: [
      // Index route - redirect based on auth status
      {
        path: '/',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <VoucherListPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      // Showcase route (previous index content)
      {
        path: '/showcase',
        element: (
          <Layout>
            <HomePage />
          </Layout>
        ),
      },
      {
        path: '/login',
        element: (
          <ProtectedRoute requireAuth={false}>
            <LoginPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/so-funktioniert-es',
        element: (
          <Layout>
            <HowItWorksPage />
          </Layout>
        ),
      },
      {
        path: '/auth/callback',
        element: <Callback />,
      },
      // Protected routes (require authentication)
      {
        path: '/coupons',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <VoucherListPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/coupon-details/:id',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <CouponDetailsPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/meine-gutscheine',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <MyVouchersPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/coupon-einloesen',
        element: (
          <CouponRedeemProtectedRoute>
            <Layout>
              <CouponRedeemPage />
            </Layout>
          </CouponRedeemProtectedRoute>
        ),
      },
      // Legacy routes (for backwards compatibility)
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/vouchers',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <VoucherListPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/balance',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <BalanceHistoryPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/balance/history',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <BalanceHistoryPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/balance/transactions',
        element: (
          <ProtectedRoute requireAuth={true}>
            <Layout>
              <BalanceHistoryPage />
            </Layout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/component-library',
        element: (
          <Layout>
            <ComponentsPage />
          </Layout>
        ),
      },
      // Admin routes
      {
        path: '/admin/*',
        element: (
          <AdminProtectedRoute>
            <AdminPage />
          </AdminProtectedRoute>
        ),
      },
      // Catch-all 404 route
      {
        path: '*',
        element: <ErrorPage />,
      },
    ],
  },
]);

// eslint-disable-next-line no-console
console.warn('Authentication configuration is: ', authConfig);

const queryClient = new QueryClient();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}
createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <OidcAuthProvider {...authConfig}>
        <AuthProvider>
          <ViewModeProvider>
            <RouterProvider router={router} />
            <Toaster />
          </ViewModeProvider>
        </AuthProvider>
      </OidcAuthProvider>
    </QueryClientProvider>
  </StrictMode>
);
